import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { TwoColumn } from 'components/layout'
import { getIntegrationLogo } from 'components/integration-logos'
import { LinkedStatus } from './LinkedStatus'

interface IntegrationsSectionProps {
  projectIntegrations: ProjectIntegration[]
  integrationUserLinks: IntegrationUserLink[]
}

interface IntegrationRow {
  integration: ProjectIntegration
  link: IntegrationUserLink | undefined
}

const columnHelper = createColumnHelper<IntegrationRow>()

export const IntegrationsSection: FC<IntegrationsSectionProps> = ({
  projectIntegrations,
  integrationUserLinks,
}) => {
  const { t } = useTranslation()

  const data: IntegrationRow[] = useMemo(
    () =>
      projectIntegrations
        .slice()
        .sort((a, b) =>
          `${a.type} ${a.name}`.localeCompare(`${b.type} ${b.name}`),
        )
        .map((integration) => ({
          integration,
          link: integrationUserLinks.find(
            (link) => link.projectIntegration.id === integration.id,
          ),
        })),
    [projectIntegrations, integrationUserLinks],
  )

  const columns = [
    columnHelper.accessor('integration.name', {
      id: 'integration',
      header: t('features.usersAdmin.userDetails.integration'),
      cell: ({ getValue, row }) => {
        const integrationName = getValue()
        const logo = getIntegrationLogo(row.original.integration.type)

        return (
          <span className="flex items-center gap-3">
            {logo && (
              <img
                src={logo}
                alt={t('features.usersAdmin.userDetails.integrationLogoAlt', {
                  integration: integrationName,
                })}
                className="w-5 h-5"
              />
            )}
            {integrationName}
          </span>
        )
      },
    }),
    columnHelper.accessor((row) => Boolean(row.link), {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <LinkedStatus linked={getValue()} />,
    }),
    columnHelper.accessor(
      (row) => row.link?.sourceUser.id ?? t('common.notAvailable'),
      {
        id: 'userId',
        header: t('features.usersAdmin.userDetails.userId'),
      },
    ),
  ] as ColumnDef<IntegrationRow>[]

  return (
    <TwoColumn
      label={t('common.projectIntegrations')}
      description={t('features.usersAdmin.userDetails.integrationsDescription')}
    >
      <SimpleTable
        className="w-[825px]"
        columns={columns}
        data={data}
        defaultSortColumn="integration"
      />
    </TwoColumn>
  )
}
